import React, { useState, useEffect } from 'react';
import { useSubscription } from './subscriptioncontext';
import UpgradeModal from './upgrade';
import './permissiongate.css';

function PermissionGate({ 
    feature, 
    children, 
    fallback = null, 
    showUpgradeModal = true,
    trackUsage = false,
    usageType = null,
    featureName = ''
}) {
    const { checkPermission, trackUsage: trackFeatureUsage, loading: subscriptionLoading, subscription, isPaidUser } = useSubscription();
    const [hasAccess, setHasAccess] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showUpgrade, setShowUpgrade] = useState(false);
    const [permissionData, setPermissionData] = useState(null);

    useEffect(() => {
        checkFeatureAccess();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feature, subscriptionLoading, subscription]);

    const checkFeatureAccess = async () => {
        if (subscriptionLoading) return;
        
        try {
            setLoading(true);
            console.log('🔍 PermissionGate: Checking access for feature:', feature);
            console.log('🔍 PermissionGate: Current subscription:', subscription);
            const result = await checkPermission(feature);
            console.log('🔍 PermissionGate: Permission result:', result);
            
            // Fallback check: If backend says no access but user has active premium subscription,
            // grant access anyway (backend might not have the feature properly configured)
            let finalAccess = result.hasAccess;
            
            // If backend denied access, check if user should have access based on status/plan
            if (!result.hasAccess) {
                const resultStatus = result.status;
                const resultPlanName = result.planName || '';
                const planNameLower = resultPlanName.toLowerCase();
                
                console.log('🔍 PermissionGate: Fallback check starting', {
                    resultStatus,
                    resultPlanName,
                    subscriptionStatus: subscription?.status,
                    resultHasAccess: result.hasAccess
                });
                
                // Check multiple conditions - if ANY are true, grant access
                const isActiveStatus = resultStatus === 'active';
                const isPremiumPlan = planNameLower.includes('premium') || 
                                     planNameLower.includes('pro') || 
                                     planNameLower.includes('paid');
                const isActiveFromSubscription = subscription?.status === 'active';
                
                const shouldGrantAccess = isActiveStatus || isPremiumPlan || isActiveFromSubscription;
                
                console.log('🔍 PermissionGate: Fallback check results', {
                    isActiveStatus,
                    isPremiumPlan,
                    isActiveFromSubscription,
                    shouldGrantAccess
                });
                
                if (shouldGrantAccess) {
                    console.log('✅ PermissionGate: Backend denied but user is premium - granting access as fallback');
                    finalAccess = true;
                } else {
                    console.log('❌ PermissionGate: Access denied - user does not appear to be premium');
                }
            }
            
            setHasAccess(finalAccess);
            setPermissionData(result);
            
            if (!finalAccess && showUpgradeModal) {
                console.log('🚫 PermissionGate: Access denied - showing upgrade modal');
                setShowUpgrade(true);
            } else if (finalAccess) {
                console.log('✅ PermissionGate: Access granted');
                setShowUpgrade(false); // Make sure upgrade modal is closed if access is granted
            }
        } catch (error) {
            console.error('❌ PermissionGate: Error checking feature access:', error);
            // On error, check if user is premium as fallback
            if (subscription && (subscription.status === 'active' || isPaidUser())) {
                console.log('✅ PermissionGate: Error occurred but user is premium - granting access as fallback');
                setHasAccess(true);
            } else {
            setHasAccess(false);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleFeatureUse = async () => {
        if (trackUsage && usageType) {
            try {
                const result = await trackFeatureUsage(usageType, featureName);
                if (!result.success) {
                    console.warn('Failed to track usage:', result.error);
                    if (result.error.includes('limit exceeded')) {
                        setHasAccess(false);
                        setShowUpgrade(true);
                    }
                }
            } catch (error) {
                console.error('Error tracking usage:', error);
            }
        }
    };

    const handleUpgradeClose = () => {
        setShowUpgrade(false);
    };

    const handleUpgradeSuccess = () => {
        setShowUpgrade(false);
        checkFeatureAccess(); // Recheck permissions after upgrade
    };

    if (loading || subscriptionLoading) {
        return (
            <div className="permission-gate-loading">
                <div className="loading-spinner"></div>
                <p>Checking access...</p>
            </div>
        );
    }

    if (!hasAccess) {
        if (fallback) {
            return fallback;
        }
        
        return (
            <div className="permission-denied">
                <div className="permission-denied-content">
                    <h3>🔒 Feature Locked</h3>
                    <p>
                        This feature requires a paid subscription or you've reached your trial limit.
                    </p>
                    {permissionData?.trial && (
                        <div className="trial-info">
                            <p>
                                Trial usage: {permissionData.trial.usage_count}/{permissionData.trial.usage_limit}
                            </p>
                        </div>
                    )}
                    <button 
                        className="upgrade-btn"
                        onClick={() => setShowUpgrade(true)}
                    >
                        Upgrade to Continue
                    </button>
                </div>
                
                {showUpgrade && (
                    <UpgradeModal 
                        isOpen={showUpgrade}
                        onClose={handleUpgradeClose}
                        onSuccess={handleUpgradeSuccess}
                        feature={feature}
                        trialInfo={permissionData?.trial}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="permission-gate-granted" onClick={handleFeatureUse}>
            {children}
            
            {showUpgrade && (
                <UpgradeModal 
                    isOpen={showUpgrade}
                    onClose={handleUpgradeClose}
                    onSuccess={handleUpgradeSuccess}
                    feature={feature}
                    trialInfo={permissionData?.trial}
                />
            )}
        </div>
    );
}

export default PermissionGate; 